import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { ChevronDown, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import AssignNewExternalUserForm from 'components/IncidentConfig/AssignNewExternalUserForm';
import { registerFocIncidentRosterTourApi } from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourBridge';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useSaveRosterTemplateFromIncident } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import { useFetchIncidentWorkspaceTeamStatus } from '../../../hooks/useFetchIncidentWorkspaceTeamStatus';
import { RosterActionsProps, RosterViewMode } from './types';
import IncidentNewSeatDialog from '../IncidentNewSeatDialog';
import AddResourceToRosterDialog from './AddResourceToRosterDialog';
import AddChildPositionDialog from './AddChildPositionDialog';
import { useHwcgFlag } from 'utils/featureFlags';

const RosterActions = ({
  isDraft = false,
  viewMode,
  onViewModeChange,
  toolbarExtra,
  hideAnalytics = false,
  showList = false,
}: RosterActionsProps) => {
  const draft = useOptionalCreateRosterDraft();
  const isCreateMode = !!draft;
  const structureOnly = !!draft?.structureOnly;
  const incidentId = useActualIncidentId();
  const navigate = useNavigate();
  const { data: teamsStatus } = useFetchIncidentWorkspaceTeamStatus();
  const saveTemplate = useSaveRosterTemplateFromIncident();
  const [isNewSeatDialogOpen, setIsNewSeatDialogOpen] = useState(false);
  const [isAddResourceDialogOpen, setIsAddResourceDialogOpen] = useState(false);
  const [isAddPositionDialogOpen, setIsAddPositionDialogOpen] = useState(false);
  const [tourExternalOpen, setTourExternalOpen] = useState(false);
  const [tourExternalPositionName, setTourExternalPositionName] = useState<
    string | null
  >(null);
  const hwcg = useHwcgFlag();
  const [saveAttachOnly, setSaveAttachOnly] = useState<boolean | null>(null);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const { data: positions = [] } = useGetPositionsForIncident();
  const tourExternalPosition = useMemo(
    () =>
      (tourExternalPositionName
        ? positions.find(
            (position) => position.name === tourExternalPositionName
          )
        : undefined) ??
      positions.find((position) => position.name === 'Incident Commander') ??
      positions[0] ??
      null,
    [positions, tourExternalPositionName]
  );

  const canSave = !draft && !!incidentId;

  useEffect(() => {
    return registerFocIncidentRosterTourApi({
      // US-IR010: highlight + Add only — do not open a dialog.
      openAddMember: () => {
        window.setTimeout(() => {
          const btn = document.querySelector('[data-tour="roster-add-member"]');
          if (btn instanceof HTMLElement) btn.focus();
        }, 50);
      },
      openAddPosition: () => {
        setIsAddPositionDialogOpen(true);
      },
      openAssignExternalUser: (positionName?: string) => {
        setTourExternalPositionName(positionName ?? null);
        setTourExternalOpen(true);
      },
      closeAddDialogs: () => {
        setIsAddPositionDialogOpen(false);
        setIsAddResourceDialogOpen(false);
        setIsNewSeatDialogOpen(false);
        setTourExternalOpen(false);
      },
    });
  }, []);

  const onClickAddIncidentSeatButton = () => {
    if (!isCreateMode && teamsStatus === 'inprogress') {
      toast.success(
        'Creating team site is in progress. Please check back later.',
        {
          autoClose: 2000,
        }
      );
      return;
    }
    setIsNewSeatDialogOpen(true);
  };

  const openSaveDialog = (attachOnly: boolean) => {
    setSaveName('');
    setSaveDescription('');
    setSaveAttachOnly(attachOnly);
  };

  const closeSaveDialog = () => {
    setSaveAttachOnly(null);
    setSaveName('');
    setSaveDescription('');
  };

  const handleSaveTemplate = () => {
    const name = saveName.trim();
    if (saveAttachOnly === null || !name) return;
    saveTemplate.mutate(
      {
        incidentId,
        name,
        description: saveDescription.trim() || null,
        attachOnly: saveAttachOnly,
      },
      { onSuccess: closeSaveDialog }
    );
  };

  if (structureOnly) return null;

  return (
    <>
      <div
        className="dark tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2"
        data-tour="roster-rapid-dev"
      >
        <h4 className="m-0">
          {`${isDraft ? '[DRAFT] - ' : ''}Incident Roster`}
        </h4>
        <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {canSave ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button type="button" variant="outline" size="sm">
                    Save Current Chart
                    <ChevronDown className="tw:h-4 tw:w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent className="dark" align="end">
                <DropdownMenuItem onClick={() => openSaveDialog(false)}>
                  Save current chart as template
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openSaveDialog(true)}>
                  Save current chart as expansion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {onViewModeChange && (
            <ToggleGroup
              variant="outline"
              size="sm"
              spacing={0}
              multiple={false}
              value={[viewMode ?? 'orgchart']}
              // Base UI hands back the full selection; an empty array means the active item was clicked again, which
              // should leave the current view alone rather than clear it.
              onValueChange={(selected: string[]) => {
                const [next] = selected;
                if (next) onViewModeChange(next as RosterViewMode);
              }}
            >
              <ToggleGroupItem
                value="orgchart"
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                Org Chart View
              </ToggleGroupItem>
              <ToggleGroupItem
                value="table"
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                Table View
              </ToggleGroupItem>
              {showList ? (
                <ToggleGroupItem
                  value="list"
                  className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
                >
                  List View
                </ToggleGroupItem>
              ) : null}
              {hideAnalytics ? null : (
                <ToggleGroupItem
                  value="analytics"
                  className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
                >
                  Analytics
                </ToggleGroupItem>
              )}
            </ToggleGroup>
          )}
          {!isCreateMode ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-tour="roster-add-member"
                  >
                    + Add
                    <ChevronDown className="tw:h-4 tw:w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent className="dark" align="end">
                <DropdownMenuItem
                  onClick={() => setIsAddResourceDialogOpen(true)}
                >
                  + Add Resource
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsAddPositionDialogOpen(true)}
                >
                  + Add Position
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={onClickAddIncidentSeatButton}
          >
            Manage {isDraft ? ' (Draft)' : ''} Custom Incident Seats
          </Button>
          {canSave ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/iap/${incidentId}/qr-posters`)}
            >
              <QrCode className="tw:h-4 tw:w-4" />
              Print QR posters
            </Button>
          ) : null}
          {toolbarExtra}
        </div>
      </div>

      {isNewSeatDialogOpen && (
        <IncidentNewSeatDialog
          show={true}
          onClose={() => setIsNewSeatDialogOpen(false)}
        />
      )}

      {isAddResourceDialogOpen && (
        <AddResourceToRosterDialog
          show={true}
          isDraft={isDraft}
          onClose={() => setIsAddResourceDialogOpen(false)}
        />
      )}

      {isAddPositionDialogOpen ? (
        <AddChildPositionDialog
          show
          onClose={() => setIsAddPositionDialogOpen(false)}
        />
      ) : null}

      {tourExternalOpen && !hwcg && tourExternalPosition && incidentId ? (
        <Modal
          show
          onHide={() => setTourExternalOpen(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>Assign External User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AssignNewExternalUserForm
              incidentId={String(incidentId)}
              positionId={tourExternalPosition.id}
              positionName={tourExternalPosition.name}
              selectingLeader={false}
              closeTable={() => setTourExternalOpen(false)}
            />
          </Modal.Body>
        </Modal>
      ) : null}

      <Modal
        show={saveAttachOnly !== null}
        onHide={closeSaveDialog}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>
            {saveAttachOnly
              ? 'Save chart as expansion'
              : 'Save chart as template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="m-0">
            {saveAttachOnly
              ? 'Saves every seat on this chart as an expansion your organization can attach under a seat on any workspace.'
              : 'Saves every seat on this chart as a template your organization can load into any workspace.'}
          </p>
          <p className="text-muted mt-2 mb-3">
            Seat names, reporting lines, and ICS elements are saved. People,
            resources, and retired seats are not.
          </p>
          <label
            className="tw:mb-1 tw:block tw:text-xs tw:font-medium"
            htmlFor="roster-template-name"
          >
            Name
          </label>
          <Input
            id="roster-template-name"
            value={saveName}
            autoFocus
            onChange={(event) => setSaveName(event.target.value)}
            placeholder="Gulf Coast response chart"
          />
          <label
            className="tw:mt-3 tw:mb-1 tw:block tw:text-xs tw:font-medium"
            htmlFor="roster-template-description"
          >
            Description (optional)
          </label>
          <Textarea
            id="roster-template-description"
            value={saveDescription}
            rows={3}
            onChange={(event) => setSaveDescription(event.target.value)}
            placeholder="When this chart should be used"
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group tw:flex tw:flex-wrap tw:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={saveTemplate.isPending}
              onClick={closeSaveDialog}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saveTemplate.isPending || !saveName.trim()}
              onClick={handleSaveTemplate}
            >
              Save template
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RosterActions;
